import { Button } from "konsta/react";
import { Copy, LoaderCircle, MessageCircle } from "lucide-react";
import type { PreparedWhatsAppIntent } from "../core/use-whatsapp-intent.js";

export interface MobileWhatsAppActionsText {
  readonly send: string;
  readonly generate: string;
  readonly preparing: string;
  readonly copy: string;
  readonly copied: string;
  readonly prepareFailed: string;
  readonly retry: string;
  readonly copyFailed: string;
}

export function MobileWhatsAppActions({
  whatsapp,
  canPrepare,
  text,
  onCopied,
}: {
  readonly whatsapp: PreparedWhatsAppIntent;
  readonly canPrepare: boolean;
  readonly text: MobileWhatsAppActionsText;
  readonly onCopied: () => void;
}) {
  const ready = Boolean(whatsapp.url);

  return (
    <div
      className="contents"
      translate="no"
      data-testid="mobile-whatsapp-actions"
      data-state={ready ? "ready" : whatsapp.preparing ? "preparing" : "idle"}
    >
      <Button
        component="button"
        type="button"
        large
        rounded
        outline
        touchRipple={false}
        aria-hidden={ready}
        className={`${ready ? "!hidden" : ""} min-h-12 gap-2 !border-success !bg-white !text-success`}
        disabled={!canPrepare || whatsapp.preparing}
        onClick={whatsapp.prepare}
      >
        <LoaderCircle
          aria-hidden="true"
          className={`${whatsapp.preparing ? "" : "!hidden"} size-4 animate-spin`}
        />
        <MessageCircle
          aria-hidden="true"
          className={`${whatsapp.preparing ? "!hidden" : ""} size-4`}
        />
        <span className={whatsapp.preparing ? "!hidden" : ""}>{text.generate}</span>
        <span className={whatsapp.preparing ? "" : "!hidden"}>{text.preparing}</span>
      </Button>

      <a
        href={whatsapp.url ?? undefined}
        rel="external"
        aria-hidden={!ready}
        tabIndex={ready ? undefined : -1}
        className={`${ready ? "flex" : "!hidden"} min-h-12 w-full items-center justify-center gap-2 rounded-full border border-success bg-white px-4 font-heading text-sm font-bold text-success`}
      >
        <MessageCircle aria-hidden="true" className="size-4" />
        <span>{text.send}</span>
      </a>

      <Button
        component="button"
        type="button"
        rounded
        clear
        touchRipple={false}
        aria-hidden={!ready}
        className={`${ready ? "" : "!hidden"} min-h-10 gap-2`}
        disabled={!ready}
        onClick={() => void whatsapp.copy().then((copied) => copied && onCopied())}
      >
        <Copy aria-hidden="true" className="size-4" />
        <span className={whatsapp.copied ? "!hidden" : ""}>{text.copy}</span>
        <span className={whatsapp.copied ? "" : "!hidden"}>{text.copied}</span>
      </Button>

      <small
        role={whatsapp.failed ? "alert" : undefined}
        aria-hidden={!whatsapp.failed}
        className={`${whatsapp.failed ? "" : "!hidden"} px-1 text-center text-destructive`}
      >
        {text.prepareFailed}{" "}
        <button
          type="button"
          className="font-bold underline underline-offset-2"
          onClick={whatsapp.retry}
        >
          {text.retry}
        </button>
      </small>

      <small
        role={whatsapp.copyFailed ? "alert" : undefined}
        aria-hidden={!whatsapp.copyFailed}
        className={`${whatsapp.copyFailed ? "" : "!hidden"} px-1 text-center text-destructive`}
      >
        {text.copyFailed}
      </small>
    </div>
  );
}
